import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, FileSignature, XCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { AgreementText } from "@/components/sign/AgreementText";
import { SignForm } from "@/components/sign/SignForm";

export default function SignAgreement() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/sign/${token}`).then((r) => setA(r.data)).catch(() => setError("We couldn't find that agreement. Check the link in your email or call 310-422-0669."));
  }, [token]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="sign-notfound">{error}</div>;
  if (!a) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading your agreement…</div>;
  const signed = a.status === "signed";
  const pdfHref = `${API}${a.pdf_url.replace(/^\/api/, "")}`;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="sign-page">
      <div className="dg-card rounded-2xl p-6 sm:p-8 mb-6">
        <div className="flex items-start gap-3">
          {signed ? <CheckCircle2 className="w-8 h-8 text-emerald-400 shrink-0" /> : a.status === "voided" ? <XCircle className="w-8 h-8 text-rose-400 shrink-0" /> : <FileSignature className="w-8 h-8 text-[#FFD600] shrink-0" />}
          <div className="min-w-0">
            <div className="dg-eyebrow">{signed ? "Signed" : a.status === "voided" ? "Withdrawn" : "Ready for your signature"}</div>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold font-display" data-testid="sign-title">
              {signed ? "All signed — welcome aboard." : a.status === "voided" ? "This agreement was withdrawn." : `Partner Technician Agreement for ${a.tech_name}`}
            </h1>
            <p className="text-slate-300 mt-2 text-sm">
              {signed
                ? <>Signed by {a.content.signatures[1].name} and {a.company_signer?.name} for Dr.Gadget. Agreement {a.agreement_id}.</>
                : a.status === "voided" ? <>David withdrew this version. Call 310-422-0669 if you have questions.</>
                : <>Prepared by <strong className="text-white">{a.company_signer?.name}</strong> for Dr.Gadget. Read everything below, then fill in your details and sign at the bottom.</>}
            </p>
            {a.message && !signed && <p className="text-sm text-slate-200 mt-3 border-l-2 border-cyan-500 pl-3 whitespace-pre-wrap" data-testid="sign-message">{a.message}</p>}
            {a.status !== "voided" && (
              <a href={pdfHref} className="dg-btn-ghost rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 mt-4" data-testid="sign-pdf-link">
                <Download className="w-4 h-4" /> {signed ? "Download signed PDF" : "Download draft PDF"}
              </a>
            )}
          </div>
        </div>
      </div>
      {a.status !== "voided" && <AgreementText content={a.content} />}
      {a.status === "sent" && <div className="mt-6"><SignForm token={token} techName={a.tech_name} onSigned={setA} /></div>}
    </div>
  );
}
